import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteBulkOps } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import { BulkImportModel } from '../../../../types';
import { Messages } from '../../../utils/message-constants';
import SharedDialog from '../../../shared/shared-dialog';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteBulkOpsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedBulkOps: BulkImportModel[];
  onDeleteSuccess?: () => void;
}

export default function DeleteBulkOpsDialog({
  open,
  handleClose,
  selectedBulkOps,
  onDeleteSuccess,
}: DeleteBulkOpsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();

  const selectedBulkOpTypes = selectedBulkOps.map(
    (bulkOpModel) => bulkOpModel.bulkImport
  );
  const selectedBulkOpIds = selectedBulkOps.map(
    (bulkOpModel) => bulkOpModel.id
  );

  const moreThanOneBulkOps = selectedBulkOps.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteBulkOps(selectedBulkOpIds),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Bulk operation(s)'),
      });

      queryClient.setQueryData(
        [UserKeys.BulkOps],
        (oldData: BulkImportModel[]) =>
          oldData.filter((report) => !selectedBulkOpIds.includes(report.id))
      );
      handleClose();
      onDeleteSuccess?.();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected bulk operation(s)'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.import_export.bulk_operations.delete_bulk_operation,
        {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.bulkOperations,
          bulkOperationType: selectedBulkOpTypes,
        }
      );
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete Bulk Op${moreThanOneBulkOps ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography>
            {moreThanOneBulkOps ? (
              <>
                Delete <strong>{selectedBulkOps.length}</strong> bulk
                operations?
              </>
            ) : (
              <>
                Delete <strong>{selectedBulkOps[0].name}</strong>?
              </>
            )}
          </Typography>
        </Box>
      }
      primaryButton={{
        title: `Delete Bulk Op${moreThanOneBulkOps ? 's' : ''}`,
        action: () => mutate(),
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
